<template>
    <div class="flex flex-col items-center justify-center gap-2">
        <UAuthForm
            :schema="schema"
            :state="state"
            icon="i-lucide-user"
            :fields="fields"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            :ui="{
                leadingIcon: 'text-(--ui-text-primary)',
                header: 'mb-0',
                leading: 'mb-0',
            }"
            :submit="{
                disabled: isSubmitting,
                label: 'Reset Password',
                ui: {
                    label: 'text-(--ui-text-white) font-bold cursor-pointer',
                },
            }"
            @submit="resetPasswordWithOtp"
        >
            <template #footer>
                <div class="mt-2 space-y-2">
                    <div class="text-default text-md">
                        Already have an account ?

                        <SharedLinkButton
                            :disabled="isSubmitting"
                            class="text-primary font-bold cursor-pointer"
                            @click.prevent="openLogin"
                        >
                            Login
                        </SharedLinkButton>
                    </div>
                </div>
            </template>
        </UAuthForm>
    </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import * as z from 'zod'
import type { AuthFormField } from '~~/layers/base/types/auth'
import { authClient } from '~~/layers/base/lib/auth-client'
import { CHANGE_PASSWORD_REQUIREMENTS_MESSAGE } from '~~/layers/base/utils/passwordPolicy'

// 1. COMPOSABLES & LOCAL LOADING STATE
// Local loading state for the Better Auth request (parent doesn't drive `inProgress` today).
const isSubmitting = ref(false)

// 2. EMITS
const emit = defineEmits([
    'reset-password-with-otp-success',
    'reset-password-with-otp-failed',
    'open-login',
])

// 3. PROPS
interface Props {
    inProgress?: boolean
    email?: string
}
const props = withDefaults(defineProps<Props>(), {
    inProgress: false,
    email: '',
})

// 4. STATE
// `UPinInput` (the `type: 'otp'` field below) always models its value as an array of
// single-character digits, never a joined string — so `otp` is an array here, joined into a
// string only when actually submitting to Better Auth (see `resetPasswordWithOtp` below).
const state = reactive<{ otp: string[]; password: string; confirmPassword: string }>({
    otp: [],
    password: '',
    confirmPassword: '',
})

// 5. VALIDATION SCHEMA
const schema = z
    .object({
        confirmPassword: z
            .string({ message: 'Confirm Password is required' })
            .min(1, 'Please confirm your password')
            .regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,}$/, {
                message: CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
            }),
        otp: z
            .array(z.string(), { message: 'OTP is required' })
            .length(6, 'Enter the 6-digit code')
            .refine((digits) => /^\d{6}$/.test(digits.join('')), 'OTP must be 6 digits'),
        password: z
            .string({ message: 'Password is required' })
            .min(1, 'Password is required')
            .regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,}$/, {
                message: CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
            }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        path: ['confirmPassword'],
        message: 'Passwords does not match',
    })

// 6. FORM FIELDS
const fields: AuthFormField[] = [
    {
        name: 'otp',
        type: 'otp',
        label: 'OTP',
        otp: {
            length: 6,
            placeholder: '●',
        },
    },
    {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: 'Enter your password',
        required: true,
    },
    {
        name: 'confirmPassword',
        label: 'Re Enter Password',
        type: 'password',
        placeholder: 'Enter your password',
        required: true,
    },
] as const

// 7. METHODS
const resetPasswordWithOtp = async (payload: {
    data: { otp: string[]; password: string; confirmPassword: string }
}): Promise<void> => {
    isSubmitting.value = true
    try {
        const { error } = await authClient.emailOtp.resetPassword({
            email: props.email,
            otp: payload.data.otp.join(''),
            password: payload.data.password,
        })
        if (error) {
            throw new Error(error.message ?? 'Failed to reset password')
        }
        emit('reset-password-with-otp-success')
    } catch (error: unknown) {
        emit('reset-password-with-otp-failed', error)
    } finally {
        isSubmitting.value = false
    }
}

const openLogin = (): void => {
    emit('open-login', 'login')
}
</script>
